import {i18n} from './src/utils/i18n';
import {escapeValue} from './src/utils/strings';
import {operatorType, operatorTypes, AbstractFilters, makeFilterComponent} from "./src/abstractFilters";
import {FilterType} from "./src/enum/filterType";

/**
 * Enum for API filter operators
 * @readonly
 * @enum {string}
 */
export const APIFilterOP = {
    AND: 'and',
    OR: 'or',
    IS_NULL: 'isnull',
    IS_NOT_NULL: 'isnotnull',
    ARRAY_CONTAINS: 'contains',
    ARRAY_NOT_CONTAINS: 'notcontains',
    ARRAY_EMPTY: 'empty',
    ARRAY_NOT_EMPTY: 'notempty',
    LIKE: 'like',
    NOT_LIKE: 'notlike',
    EQUALS: 'eq',
    NOT_EQUALS: 'neq',
    GREATER_THAN: 'gt',
    LOWER_THAN: 'lt',
    GREATER_THAN_OR_EQUAL: 'gte',
    LOWER_THAN_OR_EQUAL: 'lte',
    BETWEEN: 'between',
    IN: 'in',
    NOT_IN: 'notin',
    FULL_TEXT: 'fulltext'
};

/**
 * Enum for API filter data types
 * @readonly
 * @enum {string}
 */
export const APIFilterDataType = {
    TEXT: 'text',
    BOOLEAN: 'bool',
    DATE: 'datetime',
    NUMBER: 'number', // float or integer
    ARRAY_TEXT: 'arrayText',
    ARRAY_NUMBER: 'arrayNumber' // array of floats or integers
};

/**
 * APIOperatorType type definition
 * @typedef {Object} APIOperatorType
 * @property {operatorType} type - type of operator
 * @property {APIFilterOP[]} list - list of allowed operators
 * @property {FilterValidatorRule[]} validation - list of validation functions
 * @property {function} builder - function to build API string
 * @property {function} render - function to render human-readable filter string
 * @property {operatorType[]} convertableToTypes - list of allowed types to convert to
 */

/**
 * Definitions of API filter operator types
 * - keys are {@link operatorType} enum values
 * @readonly
 * @type {Object.<string, APIOperatorType>}
 */
const APIOperatorTypes = {
    [operatorType.groupOperators]: {
        ...operatorTypes[operatorType.groupOperators],
        list: [
            APIFilterOP.AND,
            APIFilterOP.OR
        ],
        builder: (key, value) => key + ':(' + value.map(nested => makeFilterComponent(nested, APIFilters)).filter(c => c).join(';') + ')',
    },
    [operatorType.fulltextOperators]: {
        ...operatorTypes[operatorType.fulltextOperators],
        list: [
            APIFilterOP.FULL_TEXT
        ],
        builder: (key, value) => key + ':' + value,
    },
    [operatorType.unaryOperators]: {
        ...operatorTypes[operatorType.unaryOperators],
        list: [
            APIFilterOP.IS_NULL,
            APIFilterOP.IS_NOT_NULL,
            APIFilterOP.ARRAY_EMPTY,
            APIFilterOP.ARRAY_NOT_EMPTY,
        ],
        builder: (key, value) => key + ':' + value,
    },
    [operatorType.binaryOperators]: {
        ...operatorTypes[operatorType.binaryOperators],
        list: [
            APIFilterOP.LIKE,
            APIFilterOP.NOT_LIKE,
            APIFilterOP.EQUALS,
            APIFilterOP.NOT_EQUALS,
            APIFilterOP.GREATER_THAN,
            APIFilterOP.LOWER_THAN,
            APIFilterOP.GREATER_THAN_OR_EQUAL,
            APIFilterOP.LOWER_THAN_OR_EQUAL,
            APIFilterOP.ARRAY_CONTAINS,
            APIFilterOP.ARRAY_NOT_CONTAINS
        ],
        builder: (key, value) => {
            const property = Object.keys(value)[0];
            return key + ':' + property + ':' + escapeValue(value[property]);
        }
    },
    [operatorType.ternaryOperators]: {
        ...operatorTypes[operatorType.ternaryOperators],
        list: [
            APIFilterOP.BETWEEN
        ],
        builder: (key, value) => {
            const property = Object.keys(value)[0];
            return key + ':' + property + ':' + escapeValue(value[property][0]) + ':' + escapeValue(value[property][1]);
        }
    },
    [operatorType.arrayOperators]: {
        ...operatorTypes[operatorType.arrayOperators],
        list: [
            APIFilterOP.IN,
            APIFilterOP.NOT_IN
        ],
        builder: (key, value) => {
            const property = Object.keys(value)[0];
            return key + ':' + property + ':(' + value[property].map(val => escapeValue(val.value || val)).join(',') + ')';
        }
    }
}

/**
 * APIFilterDataTypeAllowedOperations type definition
 * - keys are {@link APIFilterDataType} enum values
 * @typedef {Object.<string, APIFilterOP[]>} APIFilterDataTypeAllowedOperationsType
 */

/**
 * Allowed API filter operations for each data type
 * @type {APIFilterDataTypeAllowedOperationsType}
 */
export let APIFilterDataTypeAllowedOperations = {
    [APIFilterDataType.TEXT]: [
        ...APIOperatorTypes[operatorType.groupOperators].list,
        APIFilterOP.EQUALS,
        APIFilterOP.NOT_EQUALS,
        APIFilterOP.IN,
        APIFilterOP.NOT_IN,
        APIFilterOP.IS_NULL,
        APIFilterOP.IS_NOT_NULL,
        APIFilterOP.LIKE,
        APIFilterOP.NOT_LIKE,
        APIFilterOP.FULL_TEXT
    ],
    [APIFilterDataType.DATE]: [
        ...APIOperatorTypes[operatorType.groupOperators].list,
        APIFilterOP.BETWEEN,
        APIFilterOP.EQUALS,
        APIFilterOP.NOT_EQUALS,
        APIFilterOP.GREATER_THAN,
        APIFilterOP.GREATER_THAN_OR_EQUAL,
        APIFilterOP.LOWER_THAN,
        APIFilterOP.LOWER_THAN_OR_EQUAL,
        APIFilterOP.IS_NULL,
        APIFilterOP.IS_NOT_NULL,
        APIFilterOP.FULL_TEXT
    ],
    [APIFilterDataType.NUMBER]: [
        ...APIOperatorTypes[operatorType.groupOperators].list,
        APIFilterOP.BETWEEN,
        APIFilterOP.EQUALS,
        APIFilterOP.NOT_EQUALS,
        APIFilterOP.IN,
        APIFilterOP.NOT_IN,
        APIFilterOP.GREATER_THAN,
        APIFilterOP.GREATER_THAN_OR_EQUAL,
        APIFilterOP.LOWER_THAN,
        APIFilterOP.LOWER_THAN_OR_EQUAL,
        APIFilterOP.IS_NULL,
        APIFilterOP.IS_NOT_NULL,
        APIFilterOP.FULL_TEXT
    ],
    [APIFilterDataType.BOOLEAN]: [
        ...APIOperatorTypes[operatorType.groupOperators].list,
        APIFilterOP.EQUALS,
        APIFilterOP.NOT_EQUALS,
        APIFilterOP.IS_NULL,
        APIFilterOP.IS_NOT_NULL
    ],
    [APIFilterDataType.ARRAY_TEXT]: [
        ...APIOperatorTypes[operatorType.groupOperators].list,
        APIFilterOP.ARRAY_CONTAINS,
        APIFilterOP.ARRAY_NOT_CONTAINS,
        APIFilterOP.ARRAY_EMPTY,
        APIFilterOP.ARRAY_NOT_EMPTY,
        APIFilterOP.IS_NULL,
        APIFilterOP.IS_NOT_NULL,
        APIFilterOP.LIKE,
        APIFilterOP.NOT_LIKE
    ],
    [APIFilterDataType.ARRAY_NUMBER]: [
        ...APIOperatorTypes[operatorType.groupOperators].list,
        APIFilterOP.ARRAY_CONTAINS,
        APIFilterOP.ARRAY_NOT_CONTAINS,
        APIFilterOP.ARRAY_EMPTY,
        APIFilterOP.ARRAY_NOT_EMPTY,
        APIFilterOP.IS_NULL,
        APIFilterOP.IS_NOT_NULL
    ]
}

/**
 * Filters implementation for API
 */
export const APIFilters = {
    ...AbstractFilters,

    /**
     * Filter implementation type (API)
     * @type {FilterType}
     */
    type: FilterType.API,

    /**
     * Returns sort API string as: 'key:asc,key2:desc,...'
     * @param sortObject {Object}
     * @returns {string}
     * @override
     */
    makeSort: sortObject => Object.keys(sortObject).map(key => `${key}:${sortObject[key].toLowerCase()}`).join(','),

    /**
     * Returns human-readable sort string from sort object
     * @param langPath {string}
     * @param sortObject {Object}
     * @returns {string}
     * @override
     */
    makeHumanReadableSort: (langPath, sortObject) => {
        return (!sortObject || !Object.keys(sortObject).length) ? '-' : Object.keys(sortObject).map(
            key => i18n.t(langPath + key + '.name') + ' ' + i18n.t('base.sort.' + sortObject[key])
        ).join(', ' + i18n.t('base.filterConfig.sortThen') + ' ');
    },

    /**
     * Returns filter API string
     * @param filterObject {Object}
     * @returns {string}
     * @override
     */
    makeFilter: (filterObject) => {
        if (Array.isArray(filterObject)) { // implicit AND
            return filterObject.map(nested => makeFilterComponent(nested, APIFilters)).filter(c => c).join(';');
        } else {
            return makeFilterComponent(filterObject, APIFilters);
        }
    },

    /**
     * Returns filter API string as: 'key=value and key2=value2 and ...'
     * @param filterObject {Object}
     * @returns {string}
     * @override
     */
    makeSimpleFilter: (filterObject) => {
        return Object.keys(filterObject)
            .map(key => APIFilterOP.EQUALS + ':' + key + ':' + escapeValue(filterObject[key]))
            .join(',');
    },

    /**
     * Returns human-readable filter string from filter object
     * @param langPath {string}
     * @param filterObject {Object}
     * @param renderValues {Object}
     * @return {string}
     * @override
     */
    makeHumanReadable: (langPath, filterObject, renderValues) => {
        return AbstractFilters.makeHumanReadable(langPath, filterObject, renderValues, APIFilters, APIFilterOP);
    },

    /**
     * Get operator type by key
     * @param key {APIFilterOP}
     * @returns {APIOperatorType}
     * @override
     */
    getType: (key) => {
        return AbstractFilters.getType(key, APIOperatorTypes);
    },

    /**
     * Checks if operator is allowed for given data type
     * @param filterOP {APIFilterOP}
     * @param filterDataType {APIFilterDataType}
     * @returns {boolean}
     * @override
     */
    isFilterOPAllowedForDataType: (filterOP, filterDataType) => {
        return AbstractFilters.isFilterOPAllowedForDataType(filterOP, filterDataType, APIFilterDataTypeAllowedOperations);
    },

    /**
     * Sets the allowed operations for each data type
     * @param newVal {APIFilterDataTypeAllowedOperationsType}
     */
    setFiltersDataTypeAllowedOperations (newVal) {
        APIFilterDataTypeAllowedOperations = newVal;
    }
}
